import { createSHA256 } from "hash-wasm";
import { cancelledError } from "./cancelled";

// Streaming SHA-256: hashing in slices keeps memory constant, so multi-GB files work.
// (SubtleCrypto has no incremental API, and file.arrayBuffer() on a >2 GB file throws.)
//
// The next slice is read while the current one is hashed, so the disk and the CPU work at the same
// time instead of taking turns. Slices are 16 MB because two of them are held per file and six files
// hash at once.
//
// Runs both inside hashWorker.js and on the page, which is why cancelling is a callback rather than
// an AbortSignal: a signal cannot be posted to a worker.
const HASH_CHUNK = 16 * 1024 * 1024;

export default async function sha256Stream(file, onProgress, isCancelled) {
    const hasher = await createSHA256();
    hasher.init();

    const read = offset => file.slice(offset, Math.min(offset + HASH_CHUNK, file.size)).arrayBuffer();

    let next = file.size > 0 ? read(0) : null;
    for (let offset = 0; offset < file.size; offset += HASH_CHUNK) {
        if (isCancelled()) {
            // The read already started is simply dropped with the rest.
            next.catch(() => {});
            throw cancelledError();
        }
        const buffer = await next;
        const following = offset + HASH_CHUNK;
        next = following < file.size ? read(following) : null;

        hasher.update(new Uint8Array(buffer));
        onProgress && onProgress(Math.min(following, file.size), file.size);
    }
    return hasher.digest("hex");
}
